import { Button } from '@lifeomic/chroma-react/components/Button';
import { defineMessages, useIntl } from 'react-intl';
import { Edit } from '@lifeomic/chromicons';
import { FormBox } from '@lifeomic/chroma-react/components/FormBox';
import { IconButton } from '@lifeomic/chroma-react/components/IconButton';
import { Modal } from '@lifeomic/chroma-react/components/Modal';
import { TextField } from '@lifeomic/chroma-react/components/TextField';
import { Tooltip } from '@lifeomic/chroma-react/components/Tooltip';
import { useForm } from 'react-hook-form';
import { useMutation } from '@tanstack/react-query';
import { useNotify } from '../../hooks/useNotify';
import * as React from 'react';
import FileService, {
  GetFileResponse,
  useSubjectRestrictedOptions,
} from '../../services/FileService';
import useServiceAuth from '../../hooks/useServiceAuth';

const fileService = new FileService();

const messages = defineMessages({
  tooltipTitle: {
    id: 'phc-web-toolkit.fileManagementControls.fileRenameAction.tooltip',
    defaultMessage: 'Rename',
  },
  modalTitle: {
    id: 'phc-web-toolkit.fileManagementControls.fileRenameAction.modal.title',
    defaultMessage: 'Rename File',
  },
  modalCancel: {
    id: 'phc-web-toolkit.fileManagementControls.fileRenameAction.modal.cancel',
    defaultMessage: 'Cancel',
  },
  modalSave: {
    id: 'phc-web-toolkit.fileManagementControls.fileRenameAction.modal.save',
    defaultMessage: 'Save',
  },
  modalFileName: {
    id: 'phc-web-toolkit.fileManagementControls.fileRenameAction.modal.fileName',
    defaultMessage: 'File name',
  },
  renameFailure: {
    id: 'phc-web-toolkit.fileManagementControls.fileRenameAction.renameFailure',
    defaultMessage: 'Failed to update file.',
  },
  renameFailureArchive: {
    id: 'phc-web-toolkit.fileManagementControls.fileRenameAction.renameFailureArchive',
    defaultMessage:
      'This file cannot be renamed because it is currently archived.',
  },
});

interface FormValues {
  name: string;
}

interface RenameFileModalProps {
  file: GetFileResponse;
  path: string;
  isOpen?: boolean;
  onDismiss?: () => any;
  onSuccess?: (file: GetFileResponse) => void;
}

const RenameFileModal: React.FC<RenameFileModalProps> = ({
  file,
  path,
  isOpen,
  onDismiss,
  onSuccess,
}) => {
  const { register, errors, handleSubmit } = useForm<FormValues>();
  const { formatMessage } = useIntl();
  const cohortRestrictedOptions = useSubjectRestrictedOptions();

  const auth = useServiceAuth();
  const [addNotification] = useNotify();

  const { mutateAsync } = useMutation(
    async (formValues: FormValues) => {
      const newName = formValues.name.startsWith('/')
        ? formValues.name
        : `${path}/${formValues.name}`;
      return await fileService.renameFile(auth)(
        file.id,
        newName,
        cohortRestrictedOptions,
      );
    },
    {
      onSuccess: ({ data }) => {
        // TODO: If we end up using react-query for the main
        // files UI, we should also invalidate the cache for this
        // https://react-query.tanstack.com/docs/guides/updates-from-mutation-responses
        const resp: GetFileResponse = data;
        return onSuccess?.(resp);
      },
      onError: (err: any) => {
        addNotification(
          formatMessage(
            err.response.status === 422
              ? messages.renameFailureArchive
              : messages.renameFailure,
          ),
          'error',
        );
        // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
        onDismiss();
      },
    },
  );

  const handleSave = async (formValues: FormValues) => {
    await mutateAsync(formValues);
    onDismiss?.();
  };

  return (
    <Modal
      title={formatMessage(messages.modalTitle)}
      isOpen={isOpen}
      onFormSubmit={handleSubmit(handleSave)}
      onDismiss={onDismiss}
      isFormContent
      actions={
        <>
          <Button variant="text" onClick={() => onDismiss?.()}>
            {formatMessage(messages.modalCancel)}
          </Button>
          <Button type="submit">{formatMessage(messages.modalSave)}</Button>
        </>
      }
    >
      <FormBox>
        <TextField
          defaultValue={file?.name}
          errorMessage={errors?.name?.message}
          hasError={Boolean(errors?.name)}
          label={formatMessage(messages.modalFileName)}
          name="name"
          ref={register({ required: true })}
          fullWidth
        />
      </FormBox>
    </Modal>
  );
};

interface FileRenameActionProps {
  file: GetFileResponse;
  path: string;
  onRename?: (file: GetFileResponse) => void;
}

export const FileRenameAction: React.FC<FileRenameActionProps> = ({
  file,
  path,
  onRename,
}) => {
  const { formatMessage } = useIntl();

  const [isDialogOpen, setIsDialogOpen] = React.useState<boolean>(false);

  return (
    <>
      <Tooltip title={formatMessage(messages.tooltipTitle)} placement="bottom">
        <IconButton
          aria-label={formatMessage(messages.tooltipTitle)}
          aria-haspopup="dialog"
          aria-expanded={isDialogOpen}
          icon={Edit}
          onClick={e => {
            e.stopPropagation();
            e.currentTarget.blur();
            setIsDialogOpen(true);
          }}
          color="inverse"
        />
      </Tooltip>
      {isDialogOpen && (
        <RenameFileModal
          file={file}
          path={path}
          isOpen={isDialogOpen}
          onDismiss={() => {
            setIsDialogOpen(false);
          }}
          onSuccess={updatedFile => {
            onRename?.(updatedFile);
          }}
        />
      )}
    </>
  );
};
